import { Link } from "react-router-dom";
import { menuPath } from "@/domain/menus";
import { useAdminInbox } from "@/hooks/useAdminInbox";
import { useAuth } from "@/hooks/useAuth";
import { MOCK_ADMIN_APPROVALS } from "./mock";
import { SPACE_META, type AdminScope } from "@/domain/space";

/**
 * 내 업무 홈 — 관리자 전용 "관리" 블록 (B′ 미리보기 2차, 2026-10-19).
 *
 * 원칙: 일반 직원 화면은 그대로, 관리자에게만 이 블록 1개를 더한다. 숫자 + 링크만.
 * 보는 사람: 전체 관리자(그룹 항목 + 두 법인) / 중앙·경원 관리자로 등록된 사람(그 법인 항목만, D-08).
 *   - 가입 승인 대기 : 전체 관리자만(계정 생성·소속은 통합관리). 실데이터(users — 그룹 미지정·비활성) → 통합관리 › 사용자·계정
 *   - 결재 대기      : 법인별, 가상(결재 기능 없음)
 *   - 동기화 이상    : 중앙 ERP 동기화라 중앙 관리 범위 + 그 데이터를 읽을 부서 권한이 있을 때만(rules 와 같은 조건). 실데이터 — 영업 동기화 마지막 실패(sales_sync_status) / 출하량 불일치(rqc_drift_log)
 *                       문제가 없으면 "정상" 한 칩으로 접는다.
 * 두 법인 오늘 생산은 이 블록이 아니라 ① 오늘 영역이 관리자에게 두 법인 카드를 나란히 보여 준다(중복 방지).
 */

function Chip({
  label, value, to, tone, mock,
}: { label: string; value: string; to?: string; tone?: "warn" | "danger" | "ok"; mock?: boolean }) {
  const cls = `my-admin-chip${tone ? ` is-${tone}` : ""}`;
  const body = (
    <>
      <span className="my-admin-chip-lbl">{label}</span>
      <span className="my-admin-chip-val mono">{value}</span>
      {mock && <span className="my-tag is-mock">가상</span>}
    </>
  );
  return to ? <Link to={to} className={cls}>{body}</Link> : <span className={cls}>{body}</span>;
}

export function AdminPanel({ scope }: { scope: AdminScope }) {
  const { hasPerm } = useAuth();
  const seeJoongang = scope.group || scope.tenants.includes("joongang");
  // 읽을 수 있는 항목만 구독 — rules 와 같은 조건(가입 승인 = 전체 관리자, 영업 동기화 = sales.view,
  // 출하량 불일치 = shipment.view·dispatch.view). 법인 관리자라도 그 부서 권한이 없으면 칩을 숨긴다
  const showSales = seeJoongang && hasPerm("sales.view");
  const showDrift = seeJoongang && (hasPerm("shipment.view") || hasPerm("dispatch.view"));
  const inbox = useAdminInbox({ signups: scope.group, salesSync: showSales, drift: showDrift });
  const pending = inbox.pendingSignups;
  const syncIssues: { label: string; to: string; title?: string }[] = [];
  if (inbox.salesSyncFailed) syncIssues.push({ label: "영업 동기화 실패", to: menuPath("sales", "joongang"), title: inbox.salesSyncError ?? undefined });
  if (inbox.driftMonths && inbox.driftMonths > 0) syncIssues.push({ label: `출하량 불일치 ${inbox.driftMonths}개월`, to: menuPath("dispatch-view", "joongang") });

  return (
    <section className="card my-admin">
      <div className="my-admin-hd">
        <span className="card-title">관리</span>
        <span className="card-sub">
          {scope.group ? "전체 관리자" : `${scope.tenants.map((t) => SPACE_META[t].short).join("·")} 관리자`}에게만 보입니다
        </span>
      </div>
      <div className="my-admin-chips">
        {scope.group && (
          <Chip
            label="가입 승인 대기"
            value={pending == null ? "…" : `${pending}건`}
            to="/group/users"
            tone={pending ? "warn" : undefined}
          />
        )}
        {scope.tenants.map((t) => (
          <Chip
            key={t}
            label={scope.tenants.length > 1 ? `${SPACE_META[t].short} 결재 대기` : "결재 대기"}
            value={`${MOCK_ADMIN_APPROVALS[t]}건`}
            mock
          />
        ))}
        {!(showSales || showDrift) ? null : syncIssues.length === 0 ? (
          // 상태 문서를 아직 못 받았거나(불러오는 중) 기록이 없으면 "정상"이라 단정하지 않는다
          inbox.salesSyncFailed === null && inbox.driftMonths === null
            ? <Chip label="동기화" value="—" />
            : <Chip label="동기화" value="정상" tone="ok" />
        ) : (
          syncIssues.map((x) => (
            <Link key={x.label} to={x.to} className="my-admin-chip is-danger" title={x.title}>
              <span className="my-admin-chip-lbl">{x.label}</span>
            </Link>
          ))
        )}
      </div>
    </section>
  );
}
